import React, { useState, useEffect } from 'react';
import {
  Drawer,
  List,
  ListItemButton,
  ListItemText,
  ListItemIcon,
  Toolbar,
  Divider,
  Box,
  Tooltip,
  Typography,
  IconButton,
  useTheme,
  useMediaQuery,
  Avatar,
  Chip,
  Menu,
  MenuItem,
  Fade,
  Slide,
  Paper,
  ClickAwayListener,
} from '@mui/material';
import {
  Dashboard as DashboardIcon,
  Description as FreightIcon,
  Business as CompaniesIcon,
  Assessment as ReportsIcon,
  Settings as SettingsIcon,
  SupervisorAccount as UsersIcon,
  ChevronLeft as ChevronLeftIcon,
  ChevronRight as ChevronRightIcon,
  Logout,
  ExpandMore as ExpandMoreIcon,
  Check as CheckIcon,
  AccountBox as AccountIcon,
  Shield as ShieldIcon,
  AdminPanelSettings as AdminIcon,
  Person as PersonIcon,
  SwapHoriz as SwapHorizIcon,
  KeyboardArrowRight as KeyboardArrowRightIcon,
} from '@mui/icons-material';
import { Link, useLocation } from 'react-router-dom';
import { getCurrentUserRole } from '../api/userManagement';
import { supabase } from '../supabaseClient';
import ThemeToggle from './ThemeToggle';
import { useAccount } from '../contexts/AccountContext';

const drawerWidth = 260;
const collapsedWidth = 56;

interface SidebarProps {
  open: boolean;
  onToggle: () => void;
  mobileOpen: boolean;
  onMobileToggle: () => void;
}

const Sidebar: React.FC<SidebarProps> = ({
  open,
  onToggle,
  mobileOpen,
  onMobileToggle,
}) => {
  const location = useLocation();
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('md'));
  const [userRole, setUserRole] = useState<string | null>(null);
  const [userEmail, setUserEmail] = useState<string | null>(null);
  const [avatarUrl, setAvatarUrl] = useState<string | null>(null);
  const [avatarError, setAvatarError] = useState(false);

  // Account management state
  const { currentAccount, accounts, setCurrentAccount, loading: accountLoading, isImpersonating, impersonatedUser } = useAccount();

  // Hover panel state
  const [showHoverPanel, setShowHoverPanel] = useState(false);
  const [hoverTimeout, setHoverTimeout] = useState<NodeJS.Timeout | null>(null);

  useEffect(() => {
    const fetchData = async () => {
      try {
        const { role } = await getCurrentUserRole();
        setUserRole(role);

        const { data: { user } } = await supabase.auth.getUser();
        if (user) {
          setUserEmail(user.email || 'test@email.com');
          // Check for avatar in user metadata
          const avatar = user.user_metadata?.avatar_url ||
            user.user_metadata?.picture ||
            user.identities?.[0]?.identity_data?.avatar_url ||
            user.identities?.[0]?.identity_data?.picture ||
            null;
          setAvatarUrl(avatar);
          setAvatarError(false); // Reset error state when fetching new avatar
        } else {
          setUserEmail('test@email.com');
        }
      } catch (e) {
        setUserEmail('test@email.com');
        setUserRole('user');
      }
    };
    fetchData();
  }, []);

  // Handle avatar load error
  const handleAvatarError = () => {
    setAvatarError(true);
  };

  const navItems = [
    { label: 'Dashboard', path: '/', icon: DashboardIcon },
    { label: 'Records', path: '/records', icon: FreightIcon },
    { label: 'Companies', path: '/companies', icon: CompaniesIcon },
    { label: 'Reports', path: '/reports', icon: ReportsIcon },
    null, //divider
    { label: 'Account', path: '/account', icon: AccountIcon },
    { label: 'Settings', path: '/settings', icon: SettingsIcon },
  ];

  if (userRole === 'admin') {
    navItems.push({ label: 'Site Admin', path: '/admin/users', icon: UsersIcon });
  }

  const handleLogout = () => {
    localStorage.clear();
    window.location.href = '/login';
  };

  // Hover panel functions
  const handleMouseEnter = () => {
    if (hoverTimeout) {
      clearTimeout(hoverTimeout);
    }
    const timeout = setTimeout(() => {
      setShowHoverPanel(true);
    }, 200);
    setHoverTimeout(timeout);
  };

  const handleMouseLeave = () => {
    if (hoverTimeout) {
      clearTimeout(hoverTimeout);
    }
    const timeout = setTimeout(() => {
      setShowHoverPanel(false);
    }, 500);
    setHoverTimeout(timeout);
  };

  const handleAccountSwitch = (account: any) => {
    setCurrentAccount(account);
    setShowHoverPanel(false);
  };

  const getPlanBadgeColor = (planType: string) => {
    switch (planType?.toLowerCase()) {
      case 'starter': return 'default';
      case 'essentials': return 'primary';
      case 'pro': return 'secondary';
      case 'enterprise': return 'error';
      default: return 'default';
    }
  };

  const getRoleIcon = (role: string) => {
    switch (role) {
      case 'owner': return <ShieldIcon sx={{ fontSize: 14 }} />;
      case 'admin': return <AdminIcon sx={{ fontSize: 14 }} />;
      case 'member': return <PersonIcon sx={{ fontSize: 14 }} />;
      default: return <PersonIcon sx={{ fontSize: 14 }} />;
    }
  };

  const getRoleColor = (role: string) => {
    switch (role) {
      case 'owner': return 'error';
      case 'admin': return 'primary';
      case 'member': return 'default';
      default: return 'default';
    }
  };

  // Sidebar content
  const sidebarContent = (
    <Box sx={{
      height: '100%',
      display: 'flex',
      flexDirection: 'column',
      justifyContent: 'space-between',
      background: 'transparent',
      position: 'relative',
    }}>
      <Box>
        <Toolbar
          sx={{
            justifyContent: open ? 'space-between' : 'center',
            px: open ? 1.5 : 0.25,
            borderBottom: theme.palette.mode === 'dark'
              ? `1px solid rgba(255,255,255,0.09)`
              : `1px solid rgba(0,0,0,0.08)`,
            backgroundColor: theme.palette.mode === 'dark'
              ? 'rgba(255,255,255,0.03)'
              : 'rgba(0,0,0,0.02)',
          }}
        >
          {open && (
            <Typography
              variant="h6"
              noWrap
              sx={{
                fontWeight: 700,
                color: theme.palette.mode === 'dark' ? '#FFFFFF' : theme.palette.text.primary,
                fontSize: '1.23rem',
                letterSpacing: 0.2,
                textShadow: theme.palette.mode === 'dark'
                  ? '0 1px 2px rgba(0,0,0,0.12)'
                  : '0 1px 2px rgba(0,0,0,0.05)',
              }}
            >
              LOADOC
            </Typography>
          )}
          {!isMobile && (
            <IconButton
              onClick={onToggle}
              size="small"
              sx={{
                width: 26,
                height: 26,
                border: theme.palette.mode === 'dark'
                  ? `1px solid rgba(255,255,255,0.17)`
                  : `1px solid rgba(0,0,0,0.12)`,
                backgroundColor: theme.palette.mode === 'dark'
                  ? 'rgba(255,255,255,0.09)'
                  : 'rgba(0,0,0,0.04)',
                color: theme.palette.mode === 'dark' ? '#FFFFFF' : theme.palette.text.primary,
                '&:hover': {
                  backgroundColor: theme.palette.mode === 'dark'
                    ? 'rgba(255,255,255,0.18)'
                    : 'rgba(0,0,0,0.08)',
                }
              }}
            >
              {open ? <ChevronLeftIcon fontSize="small" /> : <ChevronRightIcon fontSize="small" />}
            </IconButton>
          )}
        </Toolbar>
        <List sx={{ pt: 2, px: open ? 0.5 : 0.2 }}>
          {navItems.map((item, index) => {
            if (!item) {
              return (
                <Divider key={index} sx={{
                  my: 2,
                  mx: open ? 1 : 0.5,
                  borderColor: theme.palette.mode === 'dark'
                    ? 'rgba(255,255,255,0.12)'
                    : 'rgba(0,0,0,0.08)',
                }} />
              );
            } else {
              const IconComponent = item.icon;
              const isSelected = location.pathname === item.path;
              return (
                <Tooltip
                  key={item.label}
                  title={!open ? item.label : ''}
                  placement="right"
                  arrow
                  disableHoverListener={open}
                >
                  <ListItemButton
                    component={Link}
                    to={item.path}
                    selected={isSelected}
                    sx={{
                      mb: 0.5,
                      mx: open ? 0.5 : 0.25,
                      borderRadius: 2,
                      minHeight: 48,
                      px: open ? 2 : 0.5,
                      justifyContent: open ? 'initial' : 'center',
                      transition: 'all 0.18s cubic-bezier(.22,.61,.36,1)',
                      color: theme.palette.mode === 'dark' ? '#FFFFFF' : theme.palette.text.primary,
                      '&:before': {
                        content: '""',
                        position: 'absolute',
                        top: 0,
                        left: 0,
                        right: 0,
                        bottom: 0,
                        borderRadius: 'inherit',
                        background: isSelected
                          ? theme.palette.mode === 'dark'
                            ? 'linear-gradient(45deg,rgba(255,255,255,0.18),rgba(255,255,255,0.11))'
                            : 'linear-gradient(45deg,rgba(160,171,96,0.15),rgba(160,171,96,0.08))'
                          : 'transparent',
                        transition: 'all 0.2s',
                      },
                      '&:hover:before': {
                        background: theme.palette.mode === 'dark'
                          ? 'linear-gradient(45deg,rgba(255,255,255,0.14),rgba(255,255,255,0.07))'
                          : 'linear-gradient(45deg,rgba(160,171,96,0.08),rgba(160,171,96,0.04))',
                      },
                      '&.Mui-selected': {
                        color: theme.palette.mode === 'dark' ? '#FFFFFF' : theme.palette.primary.main,
                        '& .MuiListItemIcon-root': {
                          color: theme.palette.mode === 'dark' ? '#FFFFFF' : theme.palette.primary.main,
                        },
                        '& .MuiListItemText-primary': {
                          color: theme.palette.mode === 'dark' ? '#FFFFFF' : theme.palette.primary.main,
                          fontWeight: 600,
                        },
                      },
                    }}
                  >
                    <ListItemIcon
                      sx={{
                        minWidth: 0,
                        mr: open ? 2.2 : 0,
                        ml: open ? 0 : 'auto',
                        justifyContent: 'center',
                        display: 'flex',
                        alignItems: 'center',
                        position: 'relative',
                        zIndex: 1,
                        width: open ? 'auto' : '100%',
                        color: 'inherit',
                        '& .MuiSvgIcon-root': {
                          fontSize: open ? '1.3rem' : '1.18rem',
                          filter: isSelected ? 'drop-shadow(0 1px 2px rgba(0,0,0,0.19))' : 'none',
                        }
                      }}
                    >
                      <IconComponent />
                    </ListItemIcon>
                    <ListItemText
                      primary={item.label}
                      sx={{
                        opacity: open ? 1 : 0,
                        position: 'relative',
                        zIndex: 1,
                        '& .MuiListItemText-primary': {
                          fontSize: '1rem',
                          fontWeight: isSelected ? 600 : 500,
                          lineHeight: 1.3,
                          textShadow: isSelected ? '0 1px 2px rgba(0,0,0,0.12)' : 'none',
                          color: 'inherit',
                        }
                      }}
                    />
                  </ListItemButton>
                </Tooltip>
              );
            }
          })}
        </List>
      </Box>

      {/* Minimal User Profile Hub */}
      <Box sx={{
        mt: 'auto',
        pb: open ? 2 : 1.5,
        pt: 2.5,
        px: open ? 1.5 : 0.5,
        position: 'relative',
        '&::before': {
          content: '""',
          position: 'absolute',
          top: 0,
          left: '50%',
          transform: 'translateX(-50%)',
          width: open ? '90%' : '60%',
          height: '1px',
          background: theme.palette.mode === 'dark'
            ? 'linear-gradient(90deg, transparent 0%, rgba(255,255,255,0.15) 50%, transparent 100%)'
            : 'linear-gradient(90deg, transparent 0%, rgba(0,0,0,0.12) 50%, transparent 100%)',
        }
      }}>

        {/* Minimal Profile Card */}
        <Box
          sx={{
            width: '100%',
            borderRadius: open ? 3 : 2,
            background: theme.palette.mode === 'dark'
              ? 'linear-gradient(145deg, rgba(255,255,255,0.04) 0%, rgba(255,255,255,0.02) 100%)'
              : 'linear-gradient(145deg, rgba(255,255,255,0.8) 0%, rgba(255,255,255,0.4) 100%)',
            backdropFilter: 'blur(20px)',
            border: theme.palette.mode === 'dark'
              ? '1px solid rgba(255,255,255,0.08)'
              : '1px solid rgba(0,0,0,0.06)',
            boxShadow: theme.palette.mode === 'dark'
              ? '0 4px 20px rgba(0,0,0,0.2), inset 0 1px 0 rgba(255,255,255,0.08)'
              : '0 4px 20px rgba(0,0,0,0.06), inset 0 1px 0 rgba(255,255,255,0.6)',
            overflow: 'hidden',
            position: 'relative',
            cursor: 'pointer',
            transition: 'all 0.3s cubic-bezier(0.4, 0, 0.2, 1)',
            '&:hover': {
              transform: 'translateY(-1px)',
              boxShadow: theme.palette.mode === 'dark'
                ? '0 8px 32px rgba(0,0,0,0.3), inset 0 1px 0 rgba(255,255,255,0.12)'
                : '0 8px 32px rgba(0,0,0,0.1), inset 0 1px 0 rgba(255,255,255,0.8)',
            },
          }}
          onMouseEnter={handleMouseEnter}
          onMouseLeave={handleMouseLeave}
        >

          {/* Content */}
          <Box sx={{
            position: 'relative',
            zIndex: 1,
            p: open ? 2 : 1.5,
            display: 'flex',
            flexDirection: open ? 'row' : 'column',
            alignItems: 'center',
            gap: open ? 1.5 : 1,
          }}>

            {/* Avatar with online indicator */}
            <Box sx={{
              position: 'relative',
              flexShrink: 0,
            }}>
              <Avatar
                src={avatarUrl && !avatarError ? avatarUrl : undefined}
                onError={handleAvatarError}
                sx={{
                  width: open ? 40 : 36,
                  height: open ? 40 : 36,
                  background: (!avatarUrl || avatarError) ? (
                    theme.palette.mode === 'dark'
                      ? 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)'
                      : 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)'
                  ) : 'transparent',
                  fontSize: open ? '1.1rem' : '1rem',
                  fontWeight: 700,
                  border: theme.palette.mode === 'dark'
                    ? '2px solid rgba(255,255,255,0.15)'
                    : '2px solid rgba(255,255,255,0.6)',
                  boxShadow: theme.palette.mode === 'dark'
                    ? '0 2px 12px rgba(0,0,0,0.3)'
                    : '0 2px 12px rgba(0,0,0,0.1)',
                  transition: 'all 0.3s ease',
                }}
              >
                {(!avatarUrl || avatarError) && (userEmail?.charAt(0).toUpperCase() || 'U')}
              </Avatar>

              {/* Online Indicator */}
              <Box sx={{
                position: 'absolute',
                bottom: -1,
                right: -1,
                width: open ? 12 : 10,
                height: open ? 12 : 10,
                borderRadius: '50%',
                background: 'linear-gradient(135deg, #4ade80 0%, #22c55e 100%)',
                border: theme.palette.mode === 'dark'
                  ? '2px solid rgba(255,255,255,0.2)'
                  : '2px solid rgba(255,255,255,0.8)',
                boxShadow: '0 0 6px rgba(34, 197, 94, 0.5)',
              }} />
            </Box>

            {open && (
              <Box sx={{
                flex: 1,
                minWidth: 0,
                display: 'flex',
                flexDirection: 'column',
                alignItems: 'flex-start',
              }}>
                <Typography
                  variant="body2"
                  sx={{
                    fontWeight: 600,
                    fontSize: '0.875rem',
                    color: theme.palette.mode === 'dark' ? '#fff' : '#1a1a1a',
                    mb: 0.25,
                    whiteSpace: 'nowrap',
                    textOverflow: 'ellipsis',
                    overflow: 'hidden',
                    maxWidth: '100%',
                  }}
                >
                  {userEmail?.split('@')[0] || 'User'}
                </Typography>

                {currentAccount && (
                  <Typography
                    variant="caption"
                    sx={{
                      color: theme.palette.mode === 'dark'
                        ? 'rgba(255,255,255,0.7)'
                        : 'rgba(0,0,0,0.6)',
                      fontSize: '0.75rem',
                      fontWeight: 500,
                      whiteSpace: 'nowrap',
                      textOverflow: 'ellipsis',
                      overflow: 'hidden',
                      maxWidth: '100%',
                    }}
                  >
                    {currentAccount.account_name}
                  </Typography>
                )}
              </Box>
            )}

            {/* Hover Indicator - only show when not collapsed */}
            {open && (
              <Box sx={{
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                opacity: 0.6,
                transition: 'all 0.3s ease',
                '&:hover': {
                  opacity: 1,
                }
              }}>
                <KeyboardArrowRightIcon
                  sx={{
                    fontSize: '1.2rem',
                    color: theme.palette.mode === 'dark'
                      ? 'rgba(255,255,255,0.5)'
                      : 'rgba(0,0,0,0.5)',
                    animation: showHoverPanel ? 'pulse 1.5s infinite' : 'none',
                    '@keyframes pulse': {
                      '0%': { opacity: 0.5 },
                      '50%': { opacity: 1 },
                      '100%': { opacity: 0.5 },
                    }
                  }}
                />
              </Box>
            )}
          </Box>
        </Box>

        {/* Branding - only show when expanded and not on mobile */}
        {open && !isMobile && (
          <Typography
            variant="caption"
            sx={{
              color: theme.palette.mode === 'dark'
                ? 'rgba(255,255,255,0.25)'
                : 'rgba(0,0,0,0.3)',
              fontSize: '0.65rem',
              textAlign: 'center',
              mt: 1.5,
              fontWeight: 500,
              letterSpacing: 0.5,
            }}
          >
            powered by{' '}
            <Typography
              component="a"
              href="https://opsolutions.io"
              target="_blank"
              rel="noopener noreferrer"
              variant="caption"
              sx={{
                color: theme.palette.mode === 'dark'
                  ? 'rgba(255,255,255,0.5)'
                  : 'rgba(0,0,0,0.5)',
                fontSize: '0.65rem',
                textDecoration: 'none',
                fontWeight: 600,
                letterSpacing: 0.5,
                '&:hover': {
                  color: theme.palette.primary.main,
                  textDecoration: 'underline',
                }
              }}
            >
              OP Solutions
            </Typography>
          </Typography>
        )}
      </Box>

      {/* Subtle Hover Panel */}
      {!isMobile && (
        <>
          {/* Invisible hover bridge */}
          {showHoverPanel && (
            <Box
              sx={{
                position: 'fixed',
                left: open ? drawerWidth - 1.5 : collapsedWidth - 1.5,
                bottom: 24,
                width: 15,
                height: 120,
                backgroundColor: 'transparent',
                zIndex: theme.zIndex.tooltip,
                pointerEvents: 'auto',
              }}
              onMouseEnter={handleMouseEnter}
              onMouseLeave={handleMouseLeave}
            />
          )}

          <Fade in={showHoverPanel} timeout={300}>
            <Paper
              sx={{
                position: 'fixed',
                left: open ? drawerWidth - 5 : collapsedWidth - 5,
                bottom: 24,
                width: 280,
                zIndex: theme.zIndex.tooltip + 1,
                background: theme.palette.mode === 'dark'
                  ? 'linear-gradient(145deg, rgba(42, 48, 58, 0.98) 0%, rgba(32, 35, 42, 0.98) 100%)'
                  : 'linear-gradient(145deg, rgba(255,255,255,0.98) 0%, rgba(248,249,250,0.98) 100%)',
                backdropFilter: 'blur(24px)',
                border: theme.palette.mode === 'dark'
                  ? '1px solid rgba(255,255,255,0.15)'
                  : '1px solid rgba(0,0,0,0.12)',
                borderRadius: 2.5,
                boxShadow: theme.palette.mode === 'dark'
                  ? '0 24px 80px rgba(0,0,0,0.5), inset 0 1px 0 rgba(255,255,255,0.15)'
                  : '0 24px 80px rgba(0,0,0,0.2), inset 0 1px 0 rgba(255,255,255,0.9)',
                overflow: 'hidden',
                transform: showHoverPanel ? 'translateX(0)' : 'translateX(-8px)',
                transition: 'all 0.3s cubic-bezier(0.4, 0, 0.2, 1)',
                // Add invisible bridge to prevent mouse leave
                '&::before': {
                  content: '""',
                  position: 'absolute',
                  left: -10,
                  top: 0,
                  bottom: 0,
                  width: 10,
                  backgroundColor: 'transparent',
                  pointerEvents: 'auto',
                }
              }}
              onMouseEnter={handleMouseEnter}
              onMouseLeave={handleMouseLeave}
            >
              <Box sx={{ p: 2.5 }}>
                {/* Header */}
                <Box sx={{ mb: 2.5 }}>
                  <Typography
                    variant="h6"
                    sx={{
                      fontWeight: 700,
                      fontSize: '1rem',
                      color: theme.palette.mode === 'dark' ? '#fff' : '#1a1a1a',
                      mb: 0.5,
                    }}
                  >
                    Account Options
                  </Typography>
                  <Typography
                    variant="body2"
                    sx={{
                      color: theme.palette.mode === 'dark'
                        ? 'rgba(255,255,255,0.7)'
                        : 'rgba(0,0,0,0.6)',
                      fontSize: '0.8rem',
                    }}
                  >
                    {userEmail}
                  </Typography>
                </Box>

                {/* Current Account Info */}
                {currentAccount && (
                  <Box sx={{
                    mb: 2.5,
                    p: 1.5,
                    borderRadius: 2,
                    background: theme.palette.mode === 'dark'
                      ? 'rgba(160,171,96,0.1)'
                      : 'rgba(160,171,96,0.08)',
                    border: theme.palette.mode === 'dark'
                      ? '1px solid rgba(160,171,96,0.2)'
                      : '1px solid rgba(160,171,96,0.15)',
                  }}>
                    <Typography variant="overline" sx={{ fontSize: '0.65rem', mb: 1, display: 'block' }}>
                      Current Account
                    </Typography>
                    <Typography variant="body2" sx={{ fontWeight: 600, mb: 1, fontSize: '0.85rem' }}>
                      {currentAccount.account_name}
                    </Typography>
                    <Box sx={{ display: 'flex', gap: 0.75, flexWrap: 'wrap' }}>
                      <Chip
                        label={currentAccount.plan_type}
                        size="small"
                        color={getPlanBadgeColor(currentAccount.plan_type) as any}
                        sx={{ height: 20, fontSize: '0.65rem', fontWeight: 600 }}
                      />
                      <Chip
                        icon={getRoleIcon(currentAccount.user_role)}
                        label={currentAccount.user_role}
                        size="small"
                        color={getRoleColor(currentAccount.user_role) as any}
                        sx={{ height: 20, fontSize: '0.65rem', fontWeight: 600 }}
                      />
                      {userRole === 'admin' && (
                        <Chip
                          label="Site Admin"
                          size="small"
                          sx={{
                            height: 20,
                            fontSize: '0.65rem',
                            fontWeight: 600,
                            background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
                            color: '#fff',
                          }}
                        />
                      )}
                    </Box>
                  </Box>
                )}

                {/* Account Switching */}
                {accounts.length > 1 && (
                  <Box sx={{ mb: 2.5 }}>
                    <Typography variant="overline" sx={{ fontSize: '0.65rem', mb: 1.2, display: 'block' }}>
                      Switch Account
                    </Typography>
                    <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.75 }}>
                      {accounts.filter(acc => acc.account_id !== currentAccount?.account_id).map((account) => (
                        <Box
                          key={account.account_id}
                          onClick={() => handleAccountSwitch(account)}
                          sx={{
                            p: 1.2,
                            borderRadius: 1.5,
                            border: theme.palette.mode === 'dark'
                              ? '1px solid rgba(255,255,255,0.1)'
                              : '1px solid rgba(0,0,0,0.08)',
                            cursor: 'pointer',
                            transition: 'all 0.2s ease',
                            '&:hover': {
                              background: theme.palette.mode === 'dark'
                                ? 'rgba(255,255,255,0.05)'
                                : 'rgba(0,0,0,0.03)',
                              transform: 'translateY(-1px)',
                            }
                          }}
                        >
                          <Typography variant="body2" sx={{ fontWeight: 500, mb: 0.4, fontSize: '0.8rem' }}>
                            {account.account_name}
                          </Typography>
                          <Box sx={{ display: 'flex', gap: 0.5 }}>
                            <Chip
                              label={account.plan_type}
                              size="small"
                              variant="outlined"
                              sx={{ height: 16, fontSize: '0.6rem' }}
                            />
                            <Chip
                              label={account.user_role}
                              size="small"
                              variant="outlined"
                              sx={{ height: 16, fontSize: '0.6rem' }}
                            />
                          </Box>
                        </Box>
                      ))}
                    </Box>
                  </Box>
                )}

                {/* Actions */}
                <Box sx={{
                  display: 'flex',
                  justifyContent: 'space-between',
                  alignItems: 'center',
                  pt: 1.5,
                  borderTop: theme.palette.mode === 'dark'
                    ? '1px solid rgba(255,255,255,0.1)'
                    : '1px solid rgba(0,0,0,0.08)',
                }}>
                  <ThemeToggle />

                  <Box sx={{ display: 'flex', gap: 0.75 }}>
                    <Tooltip title="Settings" arrow>
                      <IconButton
                        component={Link}
                        to="/settings"
                        size="small"
                        sx={{
                          background: theme.palette.mode === 'dark'
                            ? 'rgba(255,255,255,0.1)'
                            : 'rgba(0,0,0,0.05)',
                          '&:hover': {
                            background: theme.palette.primary.main,
                            color: '#fff',
                            transform: 'scale(1.05)',
                          },
                          transition: 'all 0.2s ease',
                        }}
                      >
                        <SettingsIcon fontSize="small" />
                      </IconButton>
                    </Tooltip>

                    <Tooltip title="Logout" arrow>
                      <IconButton
                        onClick={handleLogout}
                        size="small"
                        sx={{
                          background: theme.palette.mode === 'dark'
                            ? 'rgba(255,255,255,0.1)'
                            : 'rgba(0,0,0,0.05)',
                          '&:hover': {
                            background: theme.palette.error.main,
                            color: '#fff',
                            transform: 'scale(1.05)',
                          },
                          transition: 'all 0.2s ease',
                        }}
                      >
                        <Logout fontSize="small" />
                      </IconButton>
                    </Tooltip>
                  </Box>
                </Box>
              </Box>
            </Paper>
          </Fade>
        </>
      )}
    </Box>
  );

  return (
    <Box component="nav">
      <Drawer
        variant={isMobile ? "temporary" : "permanent"}
        open={isMobile ? mobileOpen : open}
        onClose={onMobileToggle}
        ModalProps={isMobile ? { keepMounted: true } : undefined}
        sx={{
          display: { xs: isMobile ? 'block' : 'none', md: isMobile ? 'none' : 'block' },
          width: open ? drawerWidth : collapsedWidth,
          flexShrink: 0,
          whiteSpace: 'nowrap',
          transition: theme.transitions.create('width', {
            easing: theme.transitions.easing.sharp,
            duration: theme.transitions.duration.leavingScreen,
          }),
          '& .MuiDrawer-paper': {
            width: open ? drawerWidth : collapsedWidth,
            transition: theme.transitions.create('width', {
              easing: theme.transitions.easing.sharp,
              duration: theme.transitions.duration.leavingScreen,
            }),
            overflowX: 'hidden',
            boxSizing: 'border-box',
            border: 'none',
            background: theme.palette.mode === 'dark'
              ? 'linear-gradient(180deg,#242A34 0%,#20232a 100%)'
              : 'linear-gradient(180deg,#ffffff 0%,#f8f9fa 100%)',
            // Adjust top positioning for impersonation banner
            top: isImpersonating && impersonatedUser ? '48px' : '0px',
            height: isImpersonating && impersonatedUser ? 'calc(100vh - 48px)' : '100vh',
          },
        }}
      >
        {sidebarContent}
      </Drawer>
    </Box>
  );
};

export default Sidebar;
